import {Badge} from '@mantine/core';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import {IconSearch} from '@tabler/icons-react';
import React, {useMemo} from 'react';
import type {NexusGraphRequest, NexusGraphResponse, NexusInFlightResponse} from 'src/api/ProactApiClient';
import { NexusNodeType, WorkflowStateEnum} from 'src/api/ProactApiClient';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {NexusEntitySearch} from './NexusEntitySearch';
import {
  formatWaitDurationHours,
  workflowEntityConfig,
} from 'src/app/workflows/workflowDesignSystem';
import {getWorkflowStateColor} from 'src/app/workflows/workflowHelpers';
import {NexusGraph} from './NexusGraph';
import {generateNexusSummarySegments} from './nexusSummary';

interface NexusDetailPanelProps {
  selectedWorkflow: NexusInFlightResponse | null;
  graphData: NexusGraphResponse | null;
  isGraphLoading: boolean;
  /** Summary fields extracted from the graph entry-point node (used when loaded via search) */
  searchSummary: SearchSummary | null;
  /** Called when the user clicks "Open" on the blocking entity */
  onOpenBlockingEntity?: (entityType: string, entityId: number) => void;
  /** Called when the user clicks the refresh button on the graph */
  onRefresh?: () => void;
  triggerGraph: (arg: NexusGraphRequest) => {unwrap: () => Promise<unknown>};
  onSearchSelect?: (entityType: string, entityId: number) => void;
}

export interface SearchSummary {
  definitionName?: string;
  internalIdentifier?: string;
  state?: string;
  createdAt?: Date;
}

const entityTypeToNodeType: Record<string, NexusNodeType> = {
  task: NexusNodeType.Task,
  workflow: NexusNodeType.Workflow,
  form: NexusNodeType.Form,
  schedule: NexusNodeType.Schedule,
  trainingSession: NexusNodeType.TrainingSession,
  itemAssignment: NexusNodeType.ItemAssignment,
};

export const NexusDetailPanel: React.FC<NexusDetailPanelProps> = ({
  selectedWorkflow,
  graphData,
  isGraphLoading,
  searchSummary,
  onOpenBlockingEntity,
  onRefresh,
  triggerGraph,
  onSearchSelect,
}) => {
  const summarySegments = useMemo(
    () => (graphData ? generateNexusSummarySegments(graphData) : []),
    [graphData]
  );

  const hasSelection = selectedWorkflow || searchSummary || graphData || isGraphLoading;

  const searchElement = (
    <NexusEntitySearch triggerGraph={triggerGraph} onSearchSelect={onSearchSelect} />
  );

  if (!hasSelection) {
    return (
      <SectionCard title="Details" icon={<IconSearch size={14} />} rightSection={searchElement}>
        <Center py="xl" px="md" mih={300}>
          <Text size="sm" c="dimmed" ta="center">
            Select a workflow from the list or search for an entity to see its causal graph.
          </Text>
        </Center>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Details" icon={<IconSearch size={14} />} rightSection={searchElement}>
      <Stack gap={0}>
        {/* Summary Header */}
        <SummaryHeader selectedWorkflow={selectedWorkflow} searchSummary={searchSummary} />

        {/* Narrative Summary */}
        {summarySegments.length > 0 && (
          <Box px="md" py="sm" style={{borderBottom: '1px solid var(--mantine-color-gray-2)'}}>
            <Text size="sm" c="dimmed">
              {summarySegments.map((seg, i) =>
                seg.emphasis
                  ? <Text key={i} span fs="italic">{seg.text}</Text>
                  : seg.text
              )}
            </Text>
          </Box>
        )}

        {/* Causal Graph */}
        <Box px="md" py="sm">
          <NexusGraph data={graphData} isLoading={isGraphLoading} compact onRefresh={onRefresh} />
        </Box>

        {/* Blocking Breakdown */}
        {selectedWorkflow?.blockingEntities?.length ? (
          <BlockingBreakdown
            blockingEntities={selectedWorkflow.blockingEntities}
            mutuallyExclusive={selectedWorkflow.mutuallyExclusive}
            onOpen={onOpenBlockingEntity}
          />
        ) : null}
      </Stack>
    </SectionCard>
  );
};

const SummaryHeader: React.FC<{
  selectedWorkflow: NexusInFlightResponse | null;
  searchSummary: SearchSummary | null;
}> = ({selectedWorkflow, searchSummary}) => {
  if (!selectedWorkflow && !searchSummary) return null;

  const definitionName = selectedWorkflow?.definitionName ?? searchSummary?.definitionName ?? '—';
  const identifier = selectedWorkflow?.internalIdentifier ?? searchSummary?.internalIdentifier ?? '—';
  const stateStr = selectedWorkflow
    ? (WorkflowStateEnum[selectedWorkflow.state] ?? String(selectedWorkflow.state))
    : (searchSummary?.state ?? '—');
  const phase = selectedWorkflow?.currentPhaseName ?? '—';
  const project = selectedWorkflow?.projectName ?? '—';
  const createdAt = selectedWorkflow?.createdAt ?? searchSummary?.createdAt;
  const waitDuration = selectedWorkflow ? formatWaitDurationHours(selectedWorkflow.waitDurationHours) : '—';

  return (
    <Box px="md" py="sm" style={{borderBottom: '1px solid var(--mantine-color-gray-2)'}}>
      <Group gap="xs" mb={4}>
        <Text fw={600} size="sm">{definitionName}</Text>
        <Text size="xs" c="dimmed">{identifier}</Text>
      </Group>
      <Group gap="md" wrap="wrap">
        {stateStr !== '—' && (
          <Badge size="sm" variant="light" color={getWorkflowStateColor(stateStr)}>
            {stateStr}
          </Badge>
        )}
        <LabeledValue label="Phase" value={phase} />
        <LabeledValue label="Project" value={project} />
        <LabeledValue
          label="Created"
          value={createdAt ? new Date(createdAt).toLocaleDateString() : '—'}
        />
        <LabeledValue label="Waiting" value={waitDuration || '—'} />
      </Group>
    </Box>
  );
};

const LabeledValue: React.FC<{label: string; value: string}> = ({label, value}) => (
  <Group gap={4}>
    <Text size="xs" c="dimmed">{label}:</Text>
    <Text size="xs">{value}</Text>
  </Group>
);

const MAX_VISIBLE_BLOCKERS = 5;

const BlockingBreakdown: React.FC<{
  blockingEntities: NexusInFlightResponse['blockingEntities'];
  mutuallyExclusive?: boolean;
  onOpen?: (entityType: string, entityId: number) => void;
}> = ({blockingEntities, mutuallyExclusive, onOpen}) => {
  const visible = blockingEntities.slice(0, MAX_VISIBLE_BLOCKERS);
  const remaining = blockingEntities.length - visible.length;
  const header = mutuallyExclusive && blockingEntities.length > 1
    ? 'Blocked by (any one)'
    : 'Blocked by';

  return (
    <Box px="md" py="sm" style={{borderTop: '1px solid var(--mantine-color-gray-2)'}}>
      <Text size="xs" c="dimmed" mb={4}>{header}</Text>
      <Stack gap={4}>
        {visible.map((entity, i) => {
          const nodeType = entityTypeToNodeType[entity.entityType ?? ''] ?? NexusNodeType.Unknown;
          const config = workflowEntityConfig[nodeType];
          const Icon = config.icon;
          const canOpen = entity.entityType && entity.entityId && onOpen;

          return (
            <Group key={i} gap="sm">
              <ThemeIcon size="sm" variant="light" color={config.color}>
                <Icon size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>{entity.name ?? 'Unknown'}</Text>
              {entity.state && (
                <Badge size="xs" variant="light" color="gray">
                  {entity.state}
                </Badge>
              )}
              {entity.assigneeName && (
                <Text size="xs" c="dimmed">{entity.assigneeName}</Text>
              )}
              {canOpen && (
                <Anchor
                  size="xs"
                  component="button"
                  onClick={() => onOpen(entity.entityType!, entity.entityId!)}
                >
                  Open {config.label} →
                </Anchor>
              )}
            </Group>
          );
        })}
        {remaining > 0 && (
          <Text size="xs" c="dimmed">+{remaining} more</Text>
        )}
      </Stack>
    </Box>
  );
};
